import { combineSlices, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { MeetingSummaryElement } from '../../apps/professional-app/fa/MeetingSummary/MeetingSummaryAgent/models'
import { AIAgentResult } from '../../models/AIAgent'
import { RoomEvent } from '../media/media'
import { selectSelectedMeetingTemplate } from '../meetingSummary/meetingTemplates'
import { parseMeetingSummary } from '../../apps/professional-app/fa/MeetingSummary/MeetingSummaryAgent/utils'
import { MeetingSummaryInteractionType } from '../../models/MeetingSummary'
import { AIAgentStatus } from '../../models/AIAgentModel'
import { hasTenMinutesPassed } from '../utils'
import { SyncStatus } from '../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/models'

export interface MeetingSummaryHeaderPreset {
  aiFeaturePresetId: string
  label: string
  icon: string
}

interface MeetingSummaryAgentState {
  isLoading: boolean
  loadingInteraction: MeetingSummaryInteractionType | null
  eventsForPollingLoading: boolean
  aiAgentModelLoading: boolean
  eventsForPolling: RoomEvent[]
  pollingEnabled: boolean
  elements: MeetingSummaryElement[]
  editedElements: MeetingSummaryElement[]
  editedLexicalState: string | null
  aiAgentId: string | null
  aiAgentResultId: string | null
  syncStatus: SyncStatus[]
  aiAgentStatus: AIAgentStatus | null
  exportingToPDF: boolean
  findAndReplaceLoading: boolean
  accumulatedMeetingSummary: string | null
}

interface MeetingSummaryAgentDisplayState {
  editing: boolean
  showChat: boolean
  displayablePresets: MeetingSummaryHeaderPreset[]
  displayedPresets: string[] | null
  shouldScrollSidebar: boolean
  embeddedAiResults: { aiFeatureId: string; aiResultId: string }[]
}

const meetingSummaryAgentInitialState: MeetingSummaryAgentState = {
  isLoading: true,
  loadingInteraction: null,
  eventsForPollingLoading: false,
  aiAgentModelLoading: false,
  pollingEnabled: false,
  eventsForPolling: [],
  elements: [],
  editedElements: [],
  editedLexicalState: null,
  aiAgentId: null,
  aiAgentResultId: null,
  syncStatus: [],
  aiAgentStatus: null,
  exportingToPDF: false,
  findAndReplaceLoading: false,
  accumulatedMeetingSummary: null,
}

const displayInitialState: MeetingSummaryAgentDisplayState = {
  editing: false,
  showChat: true,
  displayablePresets: [],
  displayedPresets: null,
  shouldScrollSidebar: false,
  embeddedAiResults: [],
}

const meetingSummarySlice = createSlice({
  name: 'meetingSummary',
  initialState: meetingSummaryAgentInitialState,
  reducers: {
    setMeetingSummaryAgentLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setMeetingSummaryAgentLoadingInteraction: (state, action: PayloadAction<MeetingSummaryInteractionType | null>) => {
      state.loadingInteraction = action.payload
    },
    setMeetingSummaryAgentEventsForPollingLoading: (state, action: PayloadAction<boolean>) => {
      state.eventsForPollingLoading = action.payload
    },
    setMeetingSummaryAiAgentModelLoading: (state, action: PayloadAction<boolean>) => {
      state.aiAgentModelLoading = action.payload
    },
    setMeetingSummaryAgentEventsForPolling: (state, action: PayloadAction<RoomEvent[]>) => {
      state.eventsForPolling = [...action.payload]
    },
    startMeetingSummaryAgentPolling: (state) => {
      state.pollingEnabled = true
    },
    stopMeetingSummaryAgentPolling: (state) => {
      state.pollingEnabled = false
    },
    setMeetingSummaryElements: (state, action: PayloadAction<MeetingSummaryElement[]>) => {
      state.elements = action.payload
    },
    setMeetingSummaryEditedContent: (state, action: PayloadAction<{ editedLexicalState: string | null; editedElements: MeetingSummaryElement[] }>) => {
      state.editedLexicalState = action.payload.editedLexicalState
      state.editedElements = action.payload.editedElements
    },
    setMeetingSummary: (state, action: PayloadAction<AIAgentResult>) => {
      const {
        originalAiAgentId,
        result: { summary },
        interaction,
        id,
        syncStatus,
      } = action.payload
      const { aiAgentId } = interaction ?? {}
      state.aiAgentId = aiAgentId ?? originalAiAgentId
      state.elements = summary ? parseMeetingSummary(summary) : []
      state.aiAgentResultId = id
      state.syncStatus = syncStatus
    },
    setAccumulatedMeetingSummary: (state, action: PayloadAction<string | null>) => {
      state.accumulatedMeetingSummary = action.payload
    },
    setMeetingSummaryElementsToAccumulatedSummary: (state) => {
      if (!state.accumulatedMeetingSummary) {
        return
      }
      state.elements = parseMeetingSummary(state.accumulatedMeetingSummary)
      state.accumulatedMeetingSummary = null
    },
    setMeetingSummaryAiAgentId: (state, action: PayloadAction<string>) => {
      state.aiAgentId = action.payload
    },
    setMeetingSummaryAiAgentStatus: (state, action: PayloadAction<AIAgentStatus>) => {
      state.aiAgentStatus = action.payload
    },
    setMeetingSummaryExportingToPDF: (state, action: PayloadAction<boolean>) => {
      state.exportingToPDF = action.payload
    },
    setMeetingSummaryFindAndReplaceLoading: (state, action: PayloadAction<boolean>) => {
      state.findAndReplaceLoading = action.payload
    },
    resetMeetingSummaryAgent: () => meetingSummaryAgentInitialState,
  },
})

const meetingSummaryAgentDisplaySlice = createSlice({
  name: 'display',
  initialState: displayInitialState,
  reducers: {
    setMeetingSummaryEditing: (state, action: PayloadAction<boolean>) => {
      state.editing = action.payload
    },
    setMeetingSummaryShowChat: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        state.shouldScrollSidebar = true
      }
      state.showChat = action.payload
    },
    setMeetingSummaryDisplayablePresets: (state, action: PayloadAction<MeetingSummaryHeaderPreset[]>) => {
      state.displayablePresets = action.payload
    },
    setMeetingSummaryDisplayedPresets: (state, action: PayloadAction<string[]>) => {
      if (state.displayedPresets && state.displayedPresets.length < action.payload.length) {
        state.shouldScrollSidebar = true
      }
      state.displayedPresets = action.payload
    },
    registerMeetingSummaryEmbeddedAiResult: (
      state,
      action: PayloadAction<{
        aiFeatureId: string
        aiResultId: string
      }>,
    ) => {
      const { aiFeatureId } = action.payload
      const newResults = state.embeddedAiResults.filter((r) => r.aiFeatureId !== aiFeatureId)
      newResults.push(action.payload)
      state.embeddedAiResults = newResults
    },
    resetMeetingSummaryAgentDisplay: () => displayInitialState,
  },
})

export const {
  setMeetingSummaryAgentLoading,
  setMeetingSummaryAgentLoadingInteraction,
  setMeetingSummaryAgentEventsForPollingLoading,
  setMeetingSummaryAiAgentModelLoading,
  startMeetingSummaryAgentPolling,
  stopMeetingSummaryAgentPolling,
  setMeetingSummaryAgentEventsForPolling,
  setMeetingSummary,
  setMeetingSummaryElements,
  setMeetingSummaryEditedContent,
  setMeetingSummaryAiAgentId,
  setMeetingSummaryAiAgentStatus,
  resetMeetingSummaryAgent,
  setMeetingSummaryExportingToPDF,
  setMeetingSummaryFindAndReplaceLoading,
  setAccumulatedMeetingSummary,
  setMeetingSummaryElementsToAccumulatedSummary,
} = meetingSummarySlice.actions
export const {
  setMeetingSummaryEditing,
  setMeetingSummaryShowChat,
  setMeetingSummaryDisplayablePresets,
  setMeetingSummaryDisplayedPresets,
  registerMeetingSummaryEmbeddedAiResult,
  resetMeetingSummaryAgentDisplay,
} = meetingSummaryAgentDisplaySlice.actions

export default combineSlices(meetingSummarySlice, meetingSummaryAgentDisplaySlice)

export const selectMeetingSummaryAgentLoading = (state: RootState) =>
  state.meetingSummaryAgent.meetingSummary.isLoading ||
  state.meetingSummaryAgent.meetingSummary.eventsForPollingLoading ||
  state.meetingSummaryAgent.meetingSummary.aiAgentModelLoading
export const selectMeetingSummaryAgentLoadingInteraction = (state: RootState) => state.meetingSummaryAgent.meetingSummary.loadingInteraction
export const selectMeetingSummaryAgentEventsForPollingLoading = (state: RootState) => state.meetingSummaryAgent.meetingSummary.eventsForPollingLoading
export const selectMeetingSummaryAgentEventsForPolling = (state: RootState) => state.meetingSummaryAgent.meetingSummary.eventsForPolling
export const selectMeetingSummaryAgentPollingEnabled = (state: RootState) => state.meetingSummaryAgent.meetingSummary.pollingEnabled
export const selectMeetingSummaryAgentElements = (state: RootState) => state.meetingSummaryAgent.meetingSummary.elements
export const selectMeetingSummaryAgentEditedElements = (state: RootState) => state.meetingSummaryAgent.meetingSummary.editedElements
export const selectMeetingSummaryAgentEditedLexicalState = (state: RootState) => state.meetingSummaryAgent.meetingSummary.editedLexicalState
export const selectMeetingSummaryExists = (state: RootState) => !!state.meetingSummaryAgent.meetingSummary.elements.length
export const selectMeetingSummaryAgentAiAgentStatus = (state: RootState) => state.meetingSummaryAgent.meetingSummary.aiAgentStatus
export const selectMeetingSummaryFindAndReplaceLoading = (state: RootState) => state.meetingSummaryAgent.meetingSummary.findAndReplaceLoading

export const selectMeetingSummaryExportingToPDF = (state: RootState) => state.meetingSummaryAgent.meetingSummary.exportingToPDF

export const selectMeetingSummarySyncStatus = (state: RootState) => state.meetingSummaryAgent.meetingSummary.syncStatus

export const selectMeetingSummaryAgentShouldPoll = createSelector(
  [
    selectMeetingSummaryAgentEventsForPollingLoading,
    selectMeetingSummaryAgentEventsForPolling,
    selectMeetingSummaryExists,
    selectSelectedMeetingTemplate,
    selectMeetingSummaryAgentAiAgentStatus,
  ],
  (eventsForPollingLoading, eventsForPolling, meetingSummaryExists, selectedMeetingTemplate, meetingSummaryAgentStatus) => {
    if (!selectedMeetingTemplate || eventsForPollingLoading) {
      return false
    }
    const aiFeatureExecutionFinished = eventsForPolling.some((e) => e.type === 'aiFeatureExecutionFinished')
    if (!aiFeatureExecutionFinished) {
      return true
    }
    if (meetingSummaryAgentStatus === AIAgentStatus.RUNNING) {
      return true
    }
    const sortedEvents = [...eventsForPolling].sort((event1, event2) => {
      const { timestamp: timestamp1 } = event1
      const { timestamp: timestamp2 } = event2
      return timestamp1.localeCompare(timestamp2)
    })
    const latestEvent = sortedEvents.pop()
    const aiFeaturesNotExecutedYet = !latestEvent || latestEvent.type === 'userAllocationUpdated'
    if (aiFeaturesNotExecutedYet) {
      return true
    }
    const oldMeetingWithoutAgentExecutedEvent = latestEvent.type === 'aiFeatureExecutionFinished'
    if (oldMeetingWithoutAgentExecutedEvent) {
      return false
    }
    const latestEventIsAgentExecutedForSelectedMeetingTemplate =
      latestEvent.type === 'meetingSummaryAgentExecuted' && latestEvent.metadata?.meetingTemplateId === selectedMeetingTemplate.id
    if (latestEventIsAgentExecutedForSelectedMeetingTemplate) {
      return !hasTenMinutesPassed(latestEvent.timestamp) && !meetingSummaryExists
    }
    return false
  },
)

export const selectMeetingSummaryAgentAiAgentId = (state: RootState) => state.meetingSummaryAgent.meetingSummary.aiAgentId
export const selectMeetingSummaryAgentAiAgentResultId = (state: RootState) => state.meetingSummaryAgent.meetingSummary.aiAgentResultId
export const selectMeetingSummaryAgentEditing = (state: RootState) => state.meetingSummaryAgent.display.editing
export const selectMeetingSummaryAgentShowChat = (state: RootState) => state.meetingSummaryAgent.display.showChat
export const selectMeetingSummaryAgentShouldScrollSidebar = (state: RootState) => state.meetingSummaryAgent.display.shouldScrollSidebar
export const selectMeetingSummaryAgentDisplayablePresets = (state: RootState) => state.meetingSummaryAgent.display.displayablePresets
export const selectMeetingSummaryAgentDisplayedPresets = createSelector(
  [(state: RootState) => state.meetingSummaryAgent.display.displayedPresets],
  (displayedPresets) => {
    return displayedPresets || []
  },
)

export const selectMeetingSummaryAgentShowSidebar = createSelector(
  [selectMeetingSummaryAgentShowChat, selectMeetingSummaryAgentDisplayedPresets],
  (showChat, displayedPresets) => showChat || !!displayedPresets.length,
)

export const selectMeetingSummaryAgentEmbeddedAiResults = (state: RootState) => state.meetingSummaryAgent.display.embeddedAiResults

export const selectIsAccumulatingMeetingSummary = (state: RootState) => {
  return state.meetingSummaryAgent.meetingSummary.accumulatedMeetingSummary !== null
}
